import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react'
import { authedFetch } from '../api'
import { Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchContainers, updateMaxContainers, updateMaxAssessments, updateMaxSummarizations, setFilterStatus } from '../slices/containersSlice'
import { fetchFailures } from '../slices/failuresSlice'
import type { RecentAssessment, RecentSummarization } from '../slices/containersSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import './ResourcesPage.css'

interface TimelineEntry {
  id: string
  type: string
  timestamp: string
  data: Record<string, unknown>
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function formatTimestamp(ts: string): string {
  const date = new Date(ts)
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return '-'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (ms < 60000) return `${Math.round(ms / 1000)}s`
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`
  return `${Math.round(ms / 3600000)}h`
}

function getEventTypeClass(type: string): string {
  if (type === 'container.error') return 'resource-event-type-error'
  if (type.startsWith('container.')) return 'resource-event-type-container'
  if (type.startsWith('task.')) return 'resource-event-type-task'
  return ''
}

function formatEventData(type: string, data: Record<string, unknown>): string {
  switch (type) {
    case 'container.started':
      return [
        data.revision && `rev: ${String(data.revision).slice(0, 8)}`,
      ].filter(Boolean).join(' \u00B7 ') || 'Started'
    case 'container.idle':
      return `${data.pendingTasks ?? 0} pending`
    case 'container.stopping':
      return 'Shutting down...'
    case 'container.stopped':
      return 'Stopped'
    case 'container.error':
      return data.error ? String(data.error).slice(0, 200) : 'Container creation failed'
    case 'task.started':
      return [
        data.skill && String(data.skill).split('/').pop(),
        data.subtasks && `${(data.subtasks as string[]).length} subtask(s)`,
      ].filter(Boolean).join(' \u00B7 ') || `attempt ${data.attempt ?? '?'}`
    case 'task.done': {
      const parts: string[] = []
      if (data.skill) parts.push(String(data.skill).split('/').pop()!)
      if (data.cost != null) parts.push(`$${Number(data.cost).toFixed(2)}`)
      if (data.failed) parts.push('FAILED')
      if (data.duration_ms != null) parts.push(`${(Number(data.duration_ms) / 1000).toFixed(1)}s`)
      return parts.join(' \u00B7 ') || 'Done'
    }
    default:
      return Object.keys(data).length > 0 ? JSON.stringify(data).slice(0, 120) : ''
  }
}

function assessmentStatusClass(status: string): string {
  if (status === 'pass') return 'resource-status-badge pass'
  if (status === 'fail') return 'resource-status-badge fail'
  if (status === 'dsq') return 'resource-status-badge dsq'
  if (status === 'disagree') return 'resource-status-badge disagree'
  if (status === 'validating') return 'resource-status-badge running'
  return 'resource-status-badge'
}

function ResourcesPage() {
  const dispatch = useAppDispatch()
  const {
    items, loading, total, activeCount, maxContainers,
    maxAssessments, maxSummarizations, assessingCount, pendingAssessmentCount, summarizingCount,
    filterStatus, recentAssessments, recentSummarizations,
  } = useAppSelector((state) => state.containers)

  const [containerLimitInput, setContainerLimitInput] = useState('')
  const [assessLimitInput, setAssessLimitInput] = useState('')
  const [summarizeLimitInput, setSummarizeLimitInput] = useState('')
  const [saving, setSaving] = useState<string | null>(null)
  const [reaping, setReaping] = useState(false)
  const [reapResult, setReapResult] = useState<{ reaped: number } | null>(null)
  const [spawning, setSpawning] = useState(false)
  const [spawnResult, setSpawnResult] = useState<{ spawned: number; reason?: string } | null>(null)
  const [clearingAssessments, setClearingAssessments] = useState(false)
  const [clearAssessResult, setClearAssessResult] = useState<{ resetCount: number } | null>(null)
  const [resettingAssessWorker, setResettingAssessWorker] = useState(false)
  const [assessWorkerResetDone, setAssessWorkerResetDone] = useState(false)
  const [expandedContainers, setExpandedContainers] = useState<Set<string>>(new Set())
  const [containerTimelines, setContainerTimelines] = useState<Record<string, TimelineEntry[]>>({})
  const [loadingEvents, setLoadingEvents] = useState<Set<string>>(new Set())
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const pageStickyHeaderRef = useRef<HTMLElement>(null)
  const [sectionHeaderStickyTop, setSectionHeaderStickyTop] = useState(0)

  useLayoutEffect(() => {
    const el = pageStickyHeaderRef.current
    if (!el) return
    const update = () => setSectionHeaderStickyTop(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const refresh = useCallback(() => {
    const filters: { status?: string } = {}
    if (filterStatus) filters.status = filterStatus
    dispatch(fetchContainers(filters))
  }, [dispatch, filterStatus])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, 10000)
    return () => clearInterval(interval)
  }, [refresh])

  useEffect(() => { setContainerLimitInput(String(maxContainers)) }, [maxContainers])
  useEffect(() => { setAssessLimitInput(String(maxAssessments)) }, [maxAssessments])
  useEffect(() => { setSummarizeLimitInput(String(maxSummarizations)) }, [maxSummarizations])

  async function handleSaveLimit(type: 'containers' | 'assessments' | 'summarizations') {
    const input = type === 'containers' ? containerLimitInput : type === 'assessments' ? assessLimitInput : summarizeLimitInput
    const val = parseInt(input, 10)
    if (isNaN(val) || val < 0 || val > 100) return
    setSaving(type)
    if (type === 'containers') await dispatch(updateMaxContainers(val))
    else if (type === 'assessments') await dispatch(updateMaxAssessments(val))
    else await dispatch(updateMaxSummarizations(val))
    setSaving(null)
  }

  const handleReapStaleContainers = useCallback(async () => {
    setReaping(true)
    setReapResult(null)
    try {
      const res = await authedFetch('/api/reap-stale-containers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (res.ok) {
        const data = await res.json()
        setReapResult({ reaped: data.reapedContainers?.length || 0 })
        refresh()
        dispatch(fetchFailures({ tag: null, search: null }))
      }
    } catch {
      // ignore
    } finally {
      setReaping(false)
    }
  }, [refresh, dispatch])

  const handleSpawnContainers = useCallback(async () => {
    setSpawning(true)
    setSpawnResult(null)
    try {
      const res = await authedFetch('/api/trigger-spawn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (res.ok) {
        const data = await res.json()
        if (data.triggered) {
          setSpawnResult({ spawned: data.pendingWork ?? 0, reason: `Spawning (${data.slotsAvailable} slots, ${data.pendingWork} pending)` })
        } else {
          setSpawnResult({ spawned: 0, reason: data.reason })
        }
        refresh()
      }
    } catch {
      // ignore
    } finally {
      setSpawning(false)
    }
  }, [refresh])

  const handleClearRunningAssessments = useCallback(async () => {
    setClearingAssessments(true)
    setClearAssessResult(null)
    try {
      const res = await authedFetch('/api/clear-running-assessments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (res.ok) {
        const data = await res.json()
        setClearAssessResult({ resetCount: data.resetCount ?? 0 })
        refresh()
      }
    } catch {
      // ignore
    } finally {
      setClearingAssessments(false)
    }
  }, [refresh])

  const handleResetAssessWorker = useCallback(async () => {
    setResettingAssessWorker(true)
    setAssessWorkerResetDone(false)
    try {
      const res = await authedFetch('/api/clear-running-assessments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (res.ok) {
        setAssessWorkerResetDone(true)
        refresh()
      }
    } catch {
      // ignore
    } finally {
      setResettingAssessWorker(false)
    }
  }, [refresh])

  const fetchContainerEvents = useCallback(async (containerName: string, isRefresh = false) => {
    if (!isRefresh && loadingEvents.has(containerName)) return
    if (!isRefresh) {
      setLoadingEvents((prev) => new Set(prev).add(containerName))
    }
    try {
      const res = await fetch(`/api/webhook-events?container_name=${encodeURIComponent(containerName)}&limit=500`)
      if (!res.ok) return
      const rawEvents = await res.json() as Array<{ id: string; type: string; timestamp: string; data: string | Record<string, unknown> }>
      const timeline: TimelineEntry[] = rawEvents.map((e) => ({
        id: e.id,
        type: e.type,
        timestamp: e.timestamp,
        data: typeof e.data === 'string' ? JSON.parse(e.data) : (e.data || {}),
      }))
      timeline.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      setContainerTimelines((prev) => ({ ...prev, [containerName]: timeline }))
    } catch {
      // ignore fetch errors
    } finally {
      if (!isRefresh) {
        setLoadingEvents((prev) => {
          const next = new Set(prev)
          next.delete(containerName)
          return next
        })
      }
    }
  }, [loadingEvents])

  // Auto-refresh expanded container timelines
  useEffect(() => {
    if (expandedContainers.size === 0) return
    const interval = setInterval(() => {
      expandedContainers.forEach((name) => fetchContainerEvents(name, true))
    }, 10000)
    return () => clearInterval(interval)
  }, [expandedContainers, fetchContainerEvents])

  const toggleContainerExpand = useCallback((containerName: string) => {
    setExpandedContainers((prev) => {
      const next = new Set(prev)
      if (next.has(containerName)) {
        next.delete(containerName)
      } else {
        next.add(containerName)
        fetchContainerEvents(containerName)
      }
      return next
    })
  }, [fetchContainerEvents])

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  return (
    <div className="resources-page">
      <SidebarNavigation />
      <main
        className="app-page-main"
        style={{
          ['--resources-section-sticky-top' as string]: `${sectionHeaderStickyTop > 0 ? sectionHeaderStickyTop : 200}px`,
        }}
      >
        <header ref={pageStickyHeaderRef} className="app-page-sticky-header">
          <div className="app-page-sticky-header-inner">
            <h1 className="app-page-title">Resources</h1>
            <div className="resources-status-cards">
              <div className="resources-card" data-testid="containers-card">
                <div className="resources-card-header">
                  <h2 className="resources-card-title">Containers</h2>
                  <span className={`resources-card-badge ${activeCount > 0 ? 'active' : ''}`}>
                    {activeCount} / {maxContainers} active
                  </span>
                </div>
                <div className="resources-card-controls">
                  <label className="resources-limit-label">Max:</label>
                  <input
                    type="number"
                    className="resources-limit-input"
                    value={containerLimitInput}
                    onChange={(e) => setContainerLimitInput(e.target.value)}
                    min={0}
                    max={100}
                  />
                  <button
                    className="resources-limit-btn"
                    onClick={() => handleSaveLimit('containers')}
                    disabled={saving === 'containers'}
                  >
                    {saving === 'containers' ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div className="resources-card" data-testid="assessments-card">
                <div className="resources-card-header">
                  <h2 className="resources-card-title">Assessments</h2>
                  <span className={`resources-card-badge ${assessingCount > 0 ? 'active' : ''}`}>
                    {assessingCount} / {maxAssessments} active{pendingAssessmentCount > 0 ? ` · ${pendingAssessmentCount} pending` : ''}
                  </span>
                </div>
                <div className="resources-card-controls">
                  <label className="resources-limit-label">Max:</label>
                  <input
                    type="number"
                    className="resources-limit-input"
                    value={assessLimitInput}
                    onChange={(e) => setAssessLimitInput(e.target.value)}
                    min={0}
                    max={100}
                  />
                  <button
                    className="resources-limit-btn"
                    onClick={() => handleSaveLimit('assessments')}
                    disabled={saving === 'assessments'}
                  >
                    {saving === 'assessments' ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div className="resources-card" data-testid="summarizations-card">
                <div className="resources-card-header">
                  <h2 className="resources-card-title">Summarizations</h2>
                  <span className={`resources-card-badge ${summarizingCount > 0 ? 'active' : ''}`}>
                    {summarizingCount} / {maxSummarizations} active
                  </span>
                </div>
                <div className="resources-card-controls">
                  <label className="resources-limit-label">Max:</label>
                  <input
                    type="number"
                    className="resources-limit-input"
                    value={summarizeLimitInput}
                    onChange={(e) => setSummarizeLimitInput(e.target.value)}
                    min={0}
                    max={100}
                  />
                  <button
                    className="resources-limit-btn"
                    onClick={() => handleSaveLimit('summarizations')}
                    disabled={saving === 'summarizations'}
                  >
                    {saving === 'summarizations' ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* === CONTAINERS SECTION === */}
        <div className="resources-section">
          <div className="resources-section-header" onClick={() => toggleSection('containers')}>
            <span className={`resource-toggle-icon ${expandedSections.has('containers') ? 'open' : ''}`}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2l8 6-8 6V2z" /></svg>
            </span>
            <h2 className="resources-section-title">Containers</h2>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
              {total} total
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                data-testid="reap-stale-containers-button"
                className="resources-reap-button"
                onClick={(e) => { e.stopPropagation(); handleReapStaleContainers() }}
                disabled={reaping}
              >
                {reaping ? 'Reaping...' : 'Reap Stale'}
              </button>
              {reapResult && (
                <span className="resources-reap-result" data-testid="reap-result">
                  {reapResult.reaped === 0
                    ? 'None found'
                    : `Reaped ${reapResult.reaped}`}
                </span>
              )}
              <button
                data-testid="spawn-containers-button"
                className="resources-reap-button"
                onClick={(e) => { e.stopPropagation(); handleSpawnContainers() }}
                disabled={spawning}
              >
                {spawning ? 'Spawning...' : 'Spawn Containers'}
              </button>
              {spawnResult && (
                <span className="resources-reap-result" data-testid="spawn-result">
                  {spawnResult.reason
                    ? spawnResult.reason
                    : spawnResult.spawned === 0
                      ? 'No work pending'
                      : `Spawned ${spawnResult.spawned}`}
                </span>
              )}
            </div>
          </div>
          {expandedSections.has('containers') && (
            <>
              <div className="resources-filters">
                <select
                  className="resources-filter-select"
                  value={filterStatus}
                  onChange={(e) => dispatch(setFilterStatus(e.target.value))}
                >
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="running">Running</option>
                  <option value="stopped">Stopped</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              {loading && items.length === 0 ? (
                <div className="resources-empty">Loading...</div>
              ) : items.length === 0 ? (
                <div className="resources-empty">No containers found</div>
              ) : (
                <table className="resources-table">
                  <colgroup>
                    <col style={{ width: 28 }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 70 }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Task</th>
                      <th>Started</th>
                      <th>Duration</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => {
                      const isExpanded = expandedContainers.has(c.container_name)
                      const timeline = containerTimelines[c.container_name] || []
                      const isLoadingEvts = loadingEvents.has(c.container_name)
                      return (
                        <React.Fragment key={c.id}>
                          <tr
                            className={`resource-row ${isExpanded ? 'expanded' : ''}`}
                            onClick={() => toggleContainerExpand(c.container_name)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="resource-toggle-cell">
                              <span className={`resource-toggle-icon ${isExpanded ? 'open' : ''}`}>
                                <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2l8 6-8 6V2z" /></svg>
                              </span>
                            </td>
                            <td className="resource-name-cell" title={c.container_name}>{c.container_name}</td>
                            <td><span className={`resource-status-badge ${c.status}`}>{c.status}</span></td>
                            <td className="resource-task-cell">
                              {c.task_info ? (
                                <Link to={c.task_info.link} className="resource-link" onClick={(e) => e.stopPropagation()}>
                                  {c.task_info.label}
                                </Link>
                              ) : c.current_task_id ? (
                                <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{c.current_task_id.slice(0, 8)}</span>
                              ) : '-'}
                            </td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{formatTime(c.created_at)}</td>
                            <td style={{ fontSize: 12 }}>{formatDuration(c.created_at, c.stopped_at)}</td>
                            <td className="resource-error-cell" title={c.error || ''}>{c.error || ''}</td>
                          </tr>
                          {isExpanded && (
                            <tr className="resource-events-row">
                              <td colSpan={7}>
                                <div className="resource-events-panel">
                                  {isLoadingEvts ? (
                                    <div className="resource-events-empty">Loading...</div>
                                  ) : timeline.length > 0 ? (
                                    <div className="resource-timeline">
                                      {timeline.map((entry) => {
                                        if (entry.type === 'log') {
                                          return (
                                            <div key={entry.id} className="timeline-entry timeline-log">
                                              <span className="timeline-time">{formatTimestamp(entry.timestamp)}</span>
                                              <span className="timeline-type-badge timeline-type-log">log</span>
                                              <span className="timeline-log-text">{String(entry.data.line || '')}</span>
                                            </div>
                                          )
                                        }
                                        if (entry.type === 'task.started' || entry.type === 'task.done') {
                                          return (
                                            <div key={entry.id} className="timeline-entry timeline-task">
                                              <div className="timeline-task-header">
                                                <span className="timeline-time">{formatTimestamp(entry.timestamp)}</span>
                                                <span className={`timeline-type-badge ${entry.type === 'task.done' ? (entry.data.failed ? 'timeline-type-task-failed' : 'timeline-type-task-done') : 'timeline-type-task-started'}`}>{entry.type}</span>
                                              </div>
                                              <pre className="timeline-task-json">{JSON.stringify(entry.data, null, 2)}</pre>
                                            </div>
                                          )
                                        }
                                        return (
                                          <div key={entry.id} className="timeline-entry timeline-event">
                                            <span className="timeline-time">{formatTimestamp(entry.timestamp)}</span>
                                            <span className={`timeline-type-badge ${getEventTypeClass(entry.type)}`}>{entry.type}</span>
                                            <span className="timeline-event-data">{formatEventData(entry.type, entry.data)}</span>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  ) : (
                                    <div className="resource-events-empty">No events or logs recorded.</div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {/* === ASSESSMENTS SECTION === */}
        <div className="resources-section">
          <div className="resources-section-header" onClick={() => toggleSection('assessments')}>
            <span className={`resource-toggle-icon ${expandedSections.has('assessments') ? 'open' : ''}`}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2l8 6-8 6V2z" /></svg>
            </span>
            <h2 className="resources-section-title">Assessments</h2>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
              {recentAssessments.length} recent
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                data-testid="clear-running-assessments-button"
                className="resources-reap-button"
                onClick={(e) => { e.stopPropagation(); handleClearRunningAssessments() }}
                disabled={clearingAssessments}
              >
                {clearingAssessments ? 'Clearing...' : 'Clear Running'}
              </button>
              {clearAssessResult && (
                <span className="resources-reap-result" data-testid="clear-assess-result">
                  {clearAssessResult.resetCount === 0
                    ? 'None found'
                    : `Reset ${clearAssessResult.resetCount}`}
                </span>
              )}
              <button
                data-testid="reset-assess-worker-button"
                className="resources-reap-button"
                onClick={(e) => { e.stopPropagation(); handleResetAssessWorker() }}
                disabled={resettingAssessWorker}
              >
                {resettingAssessWorker ? 'Resetting...' : 'Reset Worker'}
              </button>
              {assessWorkerResetDone && (
                <span className="resources-reap-result">Done</span>
              )}
            </div>
          </div>
          {expandedSections.has('assessments') && (
            recentAssessments.length === 0 ? (
              <div className="resources-empty">No recent assessments</div>
            ) : (
              <table className="resources-table">
                <thead>
                  <tr>
                    <th>Failure</th>
                    <th>Agent</th>
                    <th>Status</th>
                    <th>Cost</th>
                    <th>Started</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAssessments.map((a: RecentAssessment) => (
                    <tr key={a.id}>
                      <td>
                        <Link to={`/failures/${a.failure_id}`} className="resource-link">{a.failure_short_id}</Link>
                        {a.failure_title && (
                          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginLeft: 4 }} title={a.failure_title}>
                            {a.failure_title.length > 40 ? a.failure_title.slice(0, 40) + '...' : a.failure_title}
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{a.agent_name || '-'}</td>
                      <td><span className={assessmentStatusClass(a.status)}>{a.status}</span></td>
                      <td style={{ fontSize: 12 }}>{a.cost ? `$${Number(a.cost).toFixed(2)}` : '-'}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{a.verify_started_at ? formatTime(a.verify_started_at) : '-'}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{a.assessed_at ? formatTime(a.assessed_at) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>

        {/* === SUMMARIZATIONS SECTION === */}
        <div className="resources-section">
          <div className="resources-section-header" onClick={() => toggleSection('summarizations')}>
            <span className={`resource-toggle-icon ${expandedSections.has('summarizations') ? 'open' : ''}`}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2l8 6-8 6V2z" /></svg>
            </span>
            <h2 className="resources-section-title">Summarizations</h2>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
              {recentSummarizations.length} recent
            </span>
          </div>
          {expandedSections.has('summarizations') && (
            recentSummarizations.length === 0 ? (
              <div className="resources-empty">No recent summarizations</div>
            ) : (
              <table className="resources-table">
                <thead>
                  <tr>
                    <th>Failure</th>
                    <th>Status</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSummarizations.map((s: RecentSummarization) => (
                    <tr key={s.id}>
                      <td>
                        <Link to={`/failures/${s.id}`} className="resource-link">{s.short_id}</Link>
                        {s.title && (
                          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginLeft: 4 }} title={s.title}>
                            {s.title.length > 50 ? s.title.slice(0, 50) + '...' : s.title}
                          </span>
                        )}
                      </td>
                      <td><span className={`resource-status-badge ${s.summarize_status === 'completed' ? 'stopped' : s.summarize_status === 'running' ? 'running' : 'failed'}`}>{s.summarize_status}</span></td>
                      <td className="resource-error-cell" title={s.summarize_error || ''}>{s.summarize_error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </main>
    </div>
  )
}

export default ResourcesPage
